import React from "react";
import { Scissors, ExternalLink } from "lucide-react";
import { useQuote } from "../QuoteDialog";

const CUTTER =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/3cmlrd8a_ETD%20Vinyl%20Cutter.webp";
const WORK_1 =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/jkt3t50z_0ef2fc0038fada1a4e43159e8f061f6c.jpg";
const WORK_2 =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/p6rh6u5o_il_fullxfull.4034500286_h6da.webp";

const FEATURES = [
  "Contour-cuts decals, lettering & shapes",
  "Wall, window & door graphics",
  "Custom branded signage in school colors",
  "Easy weeding & application workflow",
];

export default function VinylCutterShowcase() {
  const { openQuote } = useQuote();

  return (
    <section
      data-testid="vinyl-cutter-showcase-section"
      className="bg-white py-24 md:py-32 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-14 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
            Vinyl cutter — in action
          </div>
          <h2
            data-testid="vinyl-cutter-headline"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-navy leading-tight"
          >
            Cut Decals & Custom Graphics —{" "}
            <span className="text-teal">On Demand.</span>
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            From wall lettering to door signage, our professional vinyl cutter
            turns any design into a clean, weed-ready decal—right inside your
            school.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Cutter machine — main feature */}
          <div
            data-testid="vinyl-cutter-machine-card"
            className="lg:col-span-7 group relative bg-[#F4F6F4] border border-slate-200 hover:border-teal/40 rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col"
          >
            <div className="cmyk-ribbon h-1 w-full" aria-hidden />
            <div className="relative aspect-[16/11] bg-white flex items-center justify-center p-6 sm:p-10 overflow-hidden">
              <img
                src={CUTTER}
                alt="ED Tech Depot vinyl cutter — Graphtec-style cutting plotter on stand"
                loading="lazy"
                decoding="async"
                className="relative max-w-full max-h-full object-contain transition-transform duration-700 group-hover:scale-105"
              />
            </div>
            <div className="p-8 md:p-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center group-hover:bg-teal transition-colors">
                  <Scissors className="w-5 h-5" strokeWidth={2} />
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal">
                  Pro-grade plotter
                </span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-bold text-navy mb-3 leading-tight">
                ED Tech Depot Vinyl Cutter
              </h3>
              <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6">
                Stand-mounted, school-ready, and built for daily use. Cuts
                decals, vinyl lettering, and contour graphics with precision
                and minimal training.
              </p>
              <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-4 pt-5 border-t border-slate-200">
                {FEATURES.map((f) => (
                  <li
                    key={f}
                    className="flex items-start gap-2 text-sm text-slate-700"
                  >
                    <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-teal" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Work examples — stacked */}
          <div className="lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            <div
              data-testid="vinyl-cutter-work-1"
              className="group relative bg-navy rounded-2xl overflow-hidden min-h-[260px]"
            >
              <img
                src={WORK_2}
                alt="School door wrapped in custom-cut vinyl: 'The Work Room' decal with icons"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal mb-1.5">
                  Door &amp; wall graphics
                </span>
                <h4 className="font-serif text-lg lg:text-xl font-bold text-white leading-snug">
                  Branded school signage
                </h4>
              </div>
            </div>
            <div
              data-testid="vinyl-cutter-work-2"
              className="group relative bg-navy rounded-2xl overflow-hidden min-h-[260px]"
            >
              <img
                src={WORK_1}
                alt="'TECHNOLOGY' wall decal with circuit-line graphic — produced by the vinyl cutter"
                loading="lazy"
                decoding="async"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/95 via-navy/40 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-6">
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal mb-1.5">
                  Wall lettering
                </span>
                <h4 className="font-serif text-lg lg:text-xl font-bold text-white leading-snug">
                  Custom contour-cut decals
                </h4>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="text-slate-700 text-base sm:text-lg max-w-2xl leading-relaxed">
            From a single classroom door to a full hallway refresh —{" "}
            <strong className="text-navy">
              we'll set you up with the right cutter and supplies.
            </strong>
          </p>
          <button
            data-testid="vinyl-cutter-quote-btn"
            onClick={openQuote}
            title="Opens in a new tab"
            className="group inline-flex items-center justify-center gap-2 bg-navy hover:bg-[#091638] text-white font-bold px-7 py-3.5 rounded-lg shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap"
          >
            Request a Quote
            <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </section>
  );
}
